import StyledInput from '@/components/styled/StyledInput';
import StyledTooltipContainer from '@/components/styled/StyledTooltipContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { Label } from '@/components/ui/label';
import StyledDatePicker from '@/components/styled/StyledDatePicker';
import StyledCheckboxGroup from '@/components/styled/StyledCheckboxGroup';
import { WEEKDAYS, WEEKDAYS_TRANSLATED } from '@/constants/weekdays';
import StyledButton from '@/components/styled/StyledButton';
import { addDays } from 'date-fns';
import {
  DoubleHotelRoomPriceRepartitionList,
  IndividualHotelRoomPriceRepartitionList,
  QuadrupleHotelRoomPriceRepartitionList,
  TripleHotelRoomPriceRepartitionList,
} from './HotelRoomTarifsRepartitions';
import { HotelRoomPricingSchema } from '@/types/benefits/hotel/HotelRoomPricingFormData';
import clsx from 'clsx';
import HotelRoomTarifCardDefaultPrices from './HotelRoomTarifCardDefaultPrices';
import RoomCategoryPaste from '../excelPaste/RoomCategoryPaste';
import RoomTariffPaste from '../excelPaste/RoomTariffPaste';
import WithExcelPastePanel from '../excelPaste/WithExcelPastePanel';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';

interface HotelRoomTarifListProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  isViewMode?: boolean;
}

const HotelRoomTarifList = ({
  control,
  index,
  isViewMode = false,
}: HotelRoomTarifListProps) => {
  const { t } = useTranslation();
  const { getValues, resetField } =
    useFormContext<HotelFormDataWithTranslations>();
  const shouldScrollToNewTarifRef = useRef(false);

  const createDefaultHotelRoomTarif = (
    applicationDays: HotelFormDataWithTranslations['hotelRoomCategories'][number]['tarifs'][number]['periods'][number]['applicationDays'] = [
      ...WEEKDAYS,
    ],
  ) =>
    ({
      name: '',
      periods: [
        {
          startDate: undefined,
          endDate: undefined,
          applicationDays,
        },
      ],
      defaultPricesExist: 'false',
      defaultPrices: {
        price: undefined,
        firstSpecialPrice: undefined,
        secondSpecialPrice: undefined,
      },
      pricePartitions: {
        individual: [],
        double: [],
        triple: [],
        quadruple: [],
      },
    }) as unknown as HotelFormDataWithTranslations['hotelRoomCategories'][number]['tarifs'][number];

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: `hotelRoomCategories.${index}.tarifs`,
  });

  const pricings = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs`,
  });
  const pricingType = useWatch({ control, name: 'pricingType' });

  // Same as the "add" button, for the tariffs a whole-category paste creates.
  const appendBlankTariffs = (count: number) => {
    const path = `hotelRoomCategories.${index}.tarifs` as const;
    const nextTarifs = [
      ...(getValues(path) ?? []),
      ...Array.from({ length: count }, () =>
        createDefaultHotelRoomTarif([...WEEKDAYS]),
      ),
    ];
    replace(nextTarifs);
    resetField(path, { defaultValue: nextTarifs });
  };

  return (
    <>
      <h2 className="text-base font-medium text-black">
        {t('hotels.roomCategory.tarifs.title')}
      </h2>
      {fields.map((field, fieldIndex) => (
        <WithExcelPastePanel
          key={`${field.id}-${fieldIndex}`}
          isViewMode={isViewMode}
          panel={
            <div className="flex flex-col gap-4">
              {/* The whole-category paste sits above the first tariff's. */}
              {fieldIndex === 0 && (
                <RoomCategoryPaste
                  categoryIndex={index}
                  pricingType={pricingType}
                  appendBlankTariffs={appendBlankTariffs}
                />
              )}
              <RoomTariffPaste
                categoryIndex={index}
                tariffIndex={fieldIndex}
                pricingType={pricingType}
              />
            </div>
          }
        >
          <HotelRoomTarifCard
            control={control}
            index={index}
            tarifIndex={fieldIndex}
            tarifFieldId={field.id}
            duplicateTarif={() =>
              (() => {
                append({
                  ...pricings[fieldIndex],
                  name: pricings[fieldIndex].name
                    ? `${pricings[fieldIndex].name} (copie)`
                    : undefined,
                });
              })()
            }
            deleteTarif={() => {
              if (fields.length > 1) {
                remove(fieldIndex);
                return;
              }
              const resetTarif = createDefaultHotelRoomTarif([...WEEKDAYS]);
              replace([resetTarif]);
              resetField(`hotelRoomCategories.${index}.tarifs.0`, {
                defaultValue: resetTarif,
              });
            }}
            isViewMode={isViewMode}
          />
        </WithExcelPastePanel>
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('hotels.roomCategory.tarifs.addTarif')}
          onClick={() =>
            (() => {
              const newTarif = createDefaultHotelRoomTarif([...WEEKDAYS]);
              const nextTarifs = [...(pricings ?? []), newTarif];
              replace(nextTarifs);
              const nextPath = `hotelRoomCategories.${index}.tarifs` as const;
              resetField(nextPath, { defaultValue: nextTarifs });
              shouldScrollToNewTarifRef.current = true;
              requestAnimationFrame(() => {
                if (!shouldScrollToNewTarifRef.current) return;
                const cards = document.querySelectorAll<HTMLElement>(
                  '[data-hotel-room-tarif-card="true"]',
                );
                const lastCard = cards[cards.length - 1];
                if (!lastCard) return;
                lastCard.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
                shouldScrollToNewTarifRef.current = false;
              });
            })()
          }
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </>
  );
};

export default HotelRoomTarifList;

interface HotelRoomTarifCardProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  tarifIndex: number;
  tarifFieldId: string;
  duplicateTarif: () => void;
  deleteTarif: () => void;
  isViewMode?: boolean;
}

const HotelRoomTarifCard = ({
  control,
  index,
  tarifIndex,
  tarifFieldId,
  isViewMode = false,
  duplicateTarif,
  deleteTarif,
}: HotelRoomTarifCardProps) => {
  const { t } = useTranslation();

  const currentTitle = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.name`,
  });
  const pricingType = useWatch({
    control,
    name: `pricingType`,
  });
  const reducedPriceExists = useWatch({
    control,
    name: `reducedPriceExists`,
  });
  const priceReductionType = useWatch({
    control,
    name: `priceReductionType`,
  });

  const [isFolded, setIsFolded] = useState(false);

  const pricingData = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}`,
  });

  const { error } = HotelRoomPricingSchema.safeParse(pricingData);

  return (
    <Card className="w-full" data-hotel-room-tarif-card="true">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle
          className="text-base leading-6 hover:cursor-pointer"
          onClick={() => setIsFolded(!isFolded)}
        >
          {currentTitle ||
            t('hotels.roomCategory.tarifs.tarif_', { index: tarifIndex + 1 })}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {!isViewMode && (
              <StyledTooltipContainer tooltipMessage={t('common.duplicate')}>
                <CopyIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => {
                    duplicateTarif();
                    setIsFolded(true);
                  }}
                />
              </StyledTooltipContainer>
            )}
            {!isViewMode && (
              <StyledTooltipContainer tooltipMessage={t('common.delete')}>
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => deleteTarif()}
                />
              </StyledTooltipContainer>
            )}
          </div>
          {isFolded ? (
            <ChevronDown
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(false)}
            />
          ) : (
            <ChevronUp
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(true)}
            />
          )}
        </div>
      </CardHeader>
      {!isFolded && (
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-col">
            {error && !isViewMode && (
              <>
                {error.issues
                  .filter(({ code }) => code === 'custom')
                  .map(({ message }, indexError) => (
                    <p
                      className="text-sm leading-6 font-medium text-error-500"
                      key={`error-${indexError}`}
                    >
                      {message}
                    </p>
                  ))}
              </>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <StyledInput
              control={control}
              name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.name`}
              placeholder="Ex: Basse saison"
              disabled={isViewMode}
            />
          </div>
          <HotelRoomPricingPeriodsList
            control={control}
            index={index}
            tarifIndex={tarifIndex}
            isViewMode={isViewMode}
          />
          <HotelRoomTarifCardDefaultPrices
            control={control}
            index={index}
            tarifIndex={tarifIndex}
            isViewMode={isViewMode}
            pricingType={pricingType}
            priceReductionType={priceReductionType}
          />
          <IndividualHotelRoomPriceRepartitionList
            control={control}
            index={index}
            tarifIndex={tarifIndex}
            tarifFieldId={tarifFieldId}
            pricingType={pricingType}
            reducedPriceExists={reducedPriceExists}
            priceReductionType={priceReductionType}
            isViewMode={isViewMode}
          />
          <DoubleHotelRoomPriceRepartitionList
            control={control}
            index={index}
            tarifIndex={tarifIndex}
            tarifFieldId={tarifFieldId}
            pricingType={pricingType}
            reducedPriceExists={reducedPriceExists}
            priceReductionType={priceReductionType}
            isViewMode={isViewMode}
          />
          <TripleHotelRoomPriceRepartitionList
            control={control}
            index={index}
            tarifIndex={tarifIndex}
            tarifFieldId={tarifFieldId}
            pricingType={pricingType}
            reducedPriceExists={reducedPriceExists}
            priceReductionType={priceReductionType}
            isViewMode={isViewMode}
          />
          <QuadrupleHotelRoomPriceRepartitionList
            control={control}
            index={index}
            tarifIndex={tarifIndex}
            tarifFieldId={tarifFieldId}
            pricingType={pricingType}
            reducedPriceExists={reducedPriceExists}
            priceReductionType={priceReductionType}
            isViewMode={isViewMode}
          />
        </CardContent>
      )}
    </Card>
  );
};

interface HotelRoomPricingPeriodsListProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  tarifIndex: number;
  isViewMode?: boolean;
}

const HotelRoomPricingPeriodsList = ({
  control,
  index,
  tarifIndex,
  isViewMode = false,
}: HotelRoomPricingPeriodsListProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.periods`,
  });

  const periodValues = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.periods`,
  });

  const getStartDateValue = (index: number, add = 0) => {
    return periodValues && periodValues[index]?.startDate
      ? addDays(new Date(periodValues[index]?.startDate), add)
      : undefined;
  };

  const getEndDateValue = (index: number, add = 0) => {
    return periodValues && periodValues[index]?.endDate
      ? addDays(new Date(periodValues[index]?.endDate), add)
      : undefined;
  };

  return (
    <>
      {fields.map((field, fieldIndex) => (
        <div className="mb-1" key={field.id}>
          <div
            className={clsx('flex flex-col', {
              'w-full gap-1.5': !isViewMode,
              'lg:flex-row md:justify-between gap-6': isViewMode,
            })}
          >
            <div className={clsx({ 'flex flex-col w-full': isViewMode })}>
              <div
                className={clsx({
                  'flex justify-between': !isViewMode,
                })}
              >
                <Label className="text-gray-700">
                  {t('hotels.roomCategory.periods.stayPeriod', {
                    index: fieldIndex + 1,
                  })}{' '}
                  *
                </Label>
                {fields.length > 1 && !isViewMode && (
                  <StyledButton
                    title={t('common.delete')}
                    className="p-0 h-auto"
                    variant="link"
                    onClick={() => remove(fieldIndex)}
                  />
                )}
              </div>
              <div
                className={clsx('flex items-center gap-2', {
                  'flex-col md:flex-row w-full': !isViewMode,
                })}
              >
                <span>{t('common.from')}</span>
                <StyledDatePicker
                  control={control}
                  name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.periods.${fieldIndex}.startDate`}
                  className="w-full"
                  isViewMode={isViewMode}
                  earliestDate={getEndDateValue(fieldIndex - 1, 1)}
                  enforceCurrentDateBoundary={false}
                />
                <span>{t('common.to')}</span>
                <StyledDatePicker
                  control={control}
                  name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.periods.${fieldIndex}.endDate`}
                  className="w-full"
                  isViewMode={isViewMode}
                  earliestDate={getStartDateValue(fieldIndex, 0)}
                  enforceCurrentDateBoundary={false}
                />
              </div>
            </div>
            <div className="flex flex-col w-full gap-1.5">
              <Label className="text-gray-700">
                {t('hotels.roomCategory.periods.possibleArrivalDays')}
              </Label>
              <StyledCheckboxGroup
                control={control}
                name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.periods.${fieldIndex}.applicationDays`}
                options={WEEKDAYS.map((weekday) => ({
                  value: weekday,
                  label: getTranslatedConst(WEEKDAYS_TRANSLATED)[weekday][0],
                }))}
                isViewMode={isViewMode}
              />
            </div>
          </div>
        </div>
      ))}
      {!isViewMode && (
        <StyledButton
          variant="link"
          className="text-brand-600 w-fit pl-2 pt-3"
          title={t('hotels.roomCategory.periods.addPeriod')}
          onClick={() => {
            append({
              startDate: undefined,
              endDate: undefined,
              applicationDays:
                periodValues &&
                periodValues.length &&
                periodValues[0].applicationDays
                  ? periodValues[0].applicationDays
                  : WEEKDAYS,
            } as unknown as HotelFormDataWithTranslations['hotelRoomCategories'][number]['tarifs'][number]['periods'][number]);
          }}
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </>
  );
};
